// src/pages/CheckoutPage.jsx
import React, { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import useCheckoutStore from "../store/useCheckoutStore";
import useShopStore from "../store/useShopStore";
import { auth } from "../firebaseConfig";
import { getShopById } from "../services/shopService";
import { getAllIngredients } from "../services/ingredientService";
import { v4 as uuidv4 } from "uuid";
import { createOrder, mapCartForServer } from "../utils/createOrder";

import ContactForm from "../components/checkout/ContactForm";
import DeliveryMethodSelector from "../components/checkout/DeliveryMethodSelector";
import PickupTimeSelector from "../components/checkout/PickupTimeSelector";
import DeliveryAddressForm from "../components/checkout/DeliveryAddressForm";
import PaymentOptions from "../components/checkout/PaymentOptions";
import CartReview from "../components/checkout/CartReview";
import OrderSummary from "../components/checkout/OrderSummary";
import NotesSection from "../components/checkout/NotesSection";

import "../styles/CheckoutPage.css";
import { formatOpeningHoursToday } from "../utils/formatOpeningHours";
import { checkCheckoutAllowed } from "../utils/runWithGuards";
import { isShopOpenNow } from "../utils/checkShopOpen";
import { fetchExtrasByIds, buildExtrasMap } from "../services/extrasService";
import useUserAddressStore from "../store/useUserAddressStore";

// Utility for handling string/number price normalization
const priceToNumber = (v) =>
  v == null
    ? 0
    : typeof v === "number"
    ? v
    : parseFloat(String(v).replace(",", ".").trim()) || 0;

const CheckoutPage = () => {
  const navigate = useNavigate();

  /** SHOP & STORE HOOKS */
  const shopId = useShopStore((state) => state.shop?.id);
  const shop = useShopStore((state) => state.shop);
  const setShopZustand = useShopStore((state) => state.setShop);
  const { cartItems, checkoutData, updateCheckoutData, resetCheckout } =
    useCheckoutStore();

  /** FIREBASE USER (can be anonymous) */
  const user = auth.currentUser;
  const isAnon = !user || user.isAnonymous === true;

  /** STATE VARIABLES */
  const [formValid, setFormValid] = useState(false);
  const [pickupError, setPickupError] = useState("");
  const [notesOpen, setNotesOpen] = useState(false);
  const [shopClosedReason, setShopClosedReason] = useState("");
  const [shopHours, setShopHours] = useState({});
  const [loading, setLoading] = useState(true);
  const [ingredientsMap, setIngredientsMap] = useState({});
  const [extrasMap, setExtrasMap] = useState({});
  const formRef = useRef();

  /** DEVICE ID (persisted per browser) */
  const [deviceId] = useState(() => {
    if (typeof window !== "undefined") {
      let id = localStorage.getItem("deviceId");
      if (!id) {
        id =
          (window.crypto?.randomUUID && window.crypto.randomUUID()) || uuidv4();
        localStorage.setItem("deviceId", id);
      }
      return id;
    }
    return null;
  });

  /** CALLBACK: when email verified & user saved in Firestore */
  const handleUserReady = ({ uid, name, isGuest }) => {
    updateCheckoutData((prev) => ({
      ...prev,
      userId: uid,
      isGuest: !!isGuest,
      contact: {
        ...prev.contact,
        firstName: prev.contact?.firstName || name || "",
      },
    }));
  };

  /** === LOAD INGREDIENTS FROM FIRESTORE === */
  useEffect(() => {
    async function fetchIngredients() {
      if (!shopId) return;
      const ingredientsArray = await getAllIngredients(shopId);
      const map = {};
      ingredientsArray.forEach((ing) => {
        map[ing.id] = ing;
      });
      setIngredientsMap(map);
    }
    fetchIngredients();
  }, [shopId]);

  /** === LOAD EXTRAS === */
  const itemExtraIds = (item) =>
    Array.isArray(item.selectedExtras)
      ? item.selectedExtras
      : item.selectedExtra
      ? [item.selectedExtra]
      : [];

  const extraIdsToLoad = useMemo(() => {
    const ids = new Set();
    for (const it of cartItems || []) {
      for (const exId of itemExtraIds(it)) ids.add(exId);
    }
    return Array.from(ids);
  }, [cartItems]);

  useEffect(() => {
    (async () => {
      if (!extraIdsToLoad.length) {
        setExtrasMap({});
        return;
      }
      const extras = await fetchExtrasByIds(extraIdsToLoad);
      setExtrasMap(buildExtrasMap(extras));
    })();
  }, [extraIdsToLoad]);

  /** === PRICE CALCULATION === */
  const subtotal = useMemo(() => {
    return cartItems.reduce((acc, item) => {
      const basePriceStr = item.prices?.[item.selectedSize] ?? item.price ?? 0;
      const base = parseFloat(basePriceStr.toString().replace(",", "."));

      // Ingredient + Extras totals
      const ingredientTotal =
        item.selectedIngredients?.reduce((sum, id) => {
          const ing = ingredientsMap[id];
          const p = (ing?.price ?? 0).toString().replace(",", ".");
          return sum + parseFloat(p || 0);
        }, 0) || 0;

      const extrasTotal =
        itemExtraIds(item).reduce((sum, id) => {
          const ex = extrasMap[id];
          const p =
            ex &&
            (typeof ex._priceNumber === "number"
              ? ex._priceNumber
              : priceToNumber(ex.price));
          return sum + (p || 0);
        }, 0) || 0;

      return (
        acc +
        (base + ingredientTotal + extrasTotal) * (Number(item.quantity) || 0)
      );
    }, 0);
  }, [cartItems, ingredientsMap, extrasMap]);

  /** === SHOP / DELIVERY FEE CALC === */
  const methodNorm = (checkoutData.method || "").trim().toLowerCase();
  const freeThreshold = priceToNumber(shop?.freeDeliveryThreshold);
  const shopDeliveryFee = priceToNumber(shop?.deliveryFee);

  const uiDeliveryFee =
    methodNorm === "delivery"
      ? freeThreshold > 0
        ? subtotal >= freeThreshold
          ? 0
          : shopDeliveryFee
        : shopDeliveryFee
      : 0;

  const uiTotal =
    methodNorm === "delivery" ? subtotal + uiDeliveryFee : subtotal;

  // Reflect totals in checkout data for backend
  useEffect(() => {
    updateCheckoutData((prev) => ({
      ...prev,
      total: uiTotal,
      deliveryFee: uiDeliveryFee,
    }));
  }, [uiTotal, uiDeliveryFee, updateCheckoutData]);

  /** === LOAD SHOP DATA + OPEN/CLOSED GUARD === */
  useEffect(() => {
    if (!shopId) return;

    async function loadShop() {
      setLoading(true);
      const shopData = await getShopById(shopId);
      if (shopData) {
        setShopZustand(shopData);

        // Extract today's hours for pickup display
        if (shopData.openingHours) {
          setShopHours(formatOpeningHoursToday(shopData.openingHours));
        }

        // Determine if shop is open
        const openByHours = shopData?.openingHours
          ? isShopOpenNow(shopData.openingHours)
          : false;

        const forceClosed =
          shopData?.forceClosed === true ||
          (shopData?.pauseOrdersUntil &&
            new Date(shopData.pauseOrdersUntil) > new Date());

        const isOpenNow = openByHours && !forceClosed;

        const { allowed, reason } = checkCheckoutAllowed({
          isShopOpen: isOpenNow,
          userAddress: checkoutData?.address?.street,
          hasItems: cartItems?.length > 0,
          minOrderAmount:
            methodNorm === "delivery" ? shopData.minOrderAmount : 0,
          cartTotal: subtotal,
          method: methodNorm,
        });

        setShopClosedReason(allowed ? "" : reason);
      }
      setLoading(false);
    }

    loadShop();
  }, [
    shopId,
    cartItems,
    checkoutData?.address?.street,
    setShopZustand,
    subtotal,
    methodNorm,
  ]);

  /** === PREFILL CONTACT INFO (FROM AUTH USER) === */
  useEffect(() => {
    if (user && user.displayName) {
      const [firstName, ...rest] = user.displayName.split(" ");
      const lastName = rest.join(" ");
      updateCheckoutData((prev) => ({
        ...prev,
        contact: {
          ...prev.contact,
          firstName: prev.contact.firstName || firstName || "",
          lastName: prev.contact.lastName || lastName || "",
        },
      }));
    }
  }, [user, updateCheckoutData]);

  /** === STEP VALIDATION FLAGS === */
  const { contact, address, notes, payment, pickupTimeType, scheduledPickup } =
    checkoutData;
  const isAnonymous = isAnon;

useEffect(() => {
  const addressSnapshot = useUserAddressStore.getState();
  console.log("📦 Zustand Address Snapshot Before Hydration:", addressSnapshot);
}, []);

  const hasName = !!contact?.firstName?.trim();
  const hasEmailVerified = contact?.emailVerified === true;
  const hasPhone = isAnonymous ? true : !!contact?.phone?.trim();

  const contactComplete = hasName && hasEmailVerified && hasPhone;
  const methodSelected = methodNorm === "delivery" || methodNorm === "pickup";

  const addressRequired = methodNorm === "delivery";
  const postcodeValid = /^\d{2}-\d{3}$/.test((address?.postcode || "").trim());
  const addressComplete = !addressRequired
    ? true
    : !!address?.street && !!address?.city && postcodeValid;

  const paymentRequired = methodNorm !== "pickup";
  const paymentComplete = !paymentRequired ? true : !!payment;

  const canSubmit =
    contactComplete &&
    methodSelected &&
    addressComplete &&
    paymentComplete &&
    !shopClosedReason;

  /** === ORDER SUBMIT (no native submit) === */
  const placeOrder = async () => {
    if (!canSubmit)
      return alert("Uzupełnij brakujące dane przed złożeniem zamówienia.");
    if (!shopId) return alert("Brak sklepu. Odśwież stronę.");

    const orderId = uuidv4();

    // Build simplified order metadata
    const sanitizedCartItems = cartItems.map((item) => {
      const extraIds = itemExtraIds(item);
      return {
        id: item.id,
        cartKey: item.cartKey,
        name: item.name?.pl || "",
        selectedSize: item.selectedSize || "",
        quantity: Number(item.quantity) || 0,
        price: Number.parseFloat(item.price),
        selectedIngredients: (item.selectedIngredients || []).map((id) => ({
          id,
          name: ingredientsMap[id]?.name?.pl || "Unknown",
          price: Number.parseFloat(
            (ingredientsMap[id]?.price ?? 0).toString().replace(",", ".")
          ),
        })),
        selectedExtras: extraIds.map((id) => ({
          id,
          name: extrasMap[id]?.name?.pl || extrasMap[id]?.name || "Extra",
          price:
            typeof extrasMap[id]?._priceNumber === "number"
              ? extrasMap[id]._priceNumber
              : priceToNumber(extrasMap[id]?.price),
        })),
      };
    });

    try {
      const resp = await createOrder({
        projectId: "bobrpizzaandburger",
        shopId,
        cartItems: mapCartForServer(cartItems),
        orderMeta: {
          method: methodNorm,
          contact,
          address,
          notes,
          payment,
          cartItems: sanitizedCartItems,
          orderId,
          userId: auth.currentUser?.uid || "guest",
          deviceInfo: navigator.userAgent || "",
          placedBy: "system",
          source: "checkoutPage",
          ...(methodNorm === "pickup"
            ? { pickupTimeType, scheduledPickup: scheduledPickup || "" }
            : {}),
        },
      });

      navigate("/order-complete", {
        state: { orderId: resp.id, totals: resp.totals },
      });
      resetCheckout();
    } catch (err) {
      console.error("createOrder failed", err);
      alert("Nie udało się utworzyć zamówienia. Spróbuj ponownie.");
    }
  };

  /** === RENDER === */
  return (
    <div className="checkout-page">
      {loading ? (
        <p>Ładowanie danych sklepu...</p>
      ) : (
        <div className="checkout-grid">
          {/* LEFT SIDE: Steps */}
          <div className="checkout-left">
            <form
              ref={formRef}
              onSubmit={(e) => {
                e.preventDefault(); // ✅ block native submit
                placeOrder(); // ✅ call our logic
              }}
              autoComplete="off"
            >
              {/* STEP 1: Contact Info (always enabled) */}
              <fieldset>
                <ContactForm
                  user={user}
                  setFormValid={setFormValid}
                  onUserReady={handleUserReady}
                  shopId={shopId}
                  deviceId={deviceId}
                />
              </fieldset>

              {/* STEP 2: Delivery Method (enabled after contact complete) */}
              <fieldset disabled={!contactComplete}>
                <DeliveryMethodSelector />
              </fieldset>

              {/* STEP 3: Address or Pickup Time */}
              {/* STEP 3A: Pickup time (only when method = pickup) */}
              <fieldset disabled={!contactComplete || methodNorm !== "pickup"}>
                {methodNorm === "pickup" && (
                  <PickupTimeSelector
                    shopHours={shopHours}
                    pickupTimeType={pickupTimeType}
                    scheduledPickup={scheduledPickup}
                    setPickupError={setPickupError}
                    pickupError={pickupError}
                  />
                )}
              </fieldset>

              {/* STEP 3B: Address (only when method = delivery) */}
              <fieldset
                disabled={!contactComplete || methodNorm !== "delivery"}
              >
                {methodNorm === "delivery" && (
                  <>
                    <DeliveryAddressForm />
                    {/* STEP 5: Optional notes */}
                    <NotesSection
                      notes={notes}
                      notesOpen={notesOpen}
                      setNotesOpen={setNotesOpen}
                    />
                  </>
                )}
              </fieldset>

              {/* STEP 4: Payment (only required for delivery) */}
              <fieldset
                disabled={
                  !contactComplete || !methodSelected || !paymentRequired
                }
              >
                {methodNorm === "delivery" && (
                  <PaymentOptions payment={payment} />
                )}
              </fieldset>
            </form>
          </div>

          {/* RIGHT SIDE: Summary & Submit */}
          <div className="checkout-right card">
            <OrderSummary
              shop={shop}
              subtotal={subtotal}
              deliveryFee={uiDeliveryFee}
              total={uiTotal}
              minOrderAmount={
                methodNorm === "delivery"
                  ? priceToNumber(shop?.minOrderAmount)
                  : 0
              }
            />

            <CartReview
              cartItems={cartItems}
              ingredientsMap={ingredientsMap}
              extrasMap={extrasMap}
              deliveryFee={shopDeliveryFee}
              freeDeliveryThreshold={freeThreshold}
              method={methodNorm}
              computedDeliveryFee={uiDeliveryFee}
            />

            {/* Missing fields list (for clarity) */}
            {!canSubmit && (
              <div className="missing-fields">
                <p>🔸 Uzupełnij brakujące dane przed złożeniem zamówienia:</p>
                <ul>
                  {!hasName && <li>Imię i nazwisko</li>}
                  {!hasEmailVerified && <li>Zweryfikuj adres e-mail</li>}
                  {!hasPhone && <li>Numer telefonu</li>}
                  {!methodSelected && <li>Wybierz sposób dostawy</li>}
                  {addressRequired && !addressComplete && (
                    <>
                      {!address?.street && <li>Ulica i numer</li>} 
                      {!address?.city && <li>Miasto</li>} 
                      {!address?.postcode && <li>Kod pocztowy (NN-NNN)</li>} 
                    </>
                  )}
                  {paymentRequired && !paymentComplete && (
                    <li>Metoda płatności</li>
                  )}
                </ul>
              </div>
            )}

            {/* Final confirm button */}
            <button
              type="button" // ✅ bulletproof: never native-submit
              className="big-confirm-button"
              onClick={placeOrder} // ✅ call logic directly
              disabled={!canSubmit}
            >
              {shopClosedReason ? shopClosedReason : "Potwierdź zamówienie"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CheckoutPage;
